import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { forkJoin, Observable } from 'rxjs';
import { RedirectSSOUrlResponse, SetCustomerProfileInformation, SetProfileInformationDTO } from '@ridango/core';
import { AuthService } from '@ridango/core';
import Env from '@ridango/core/env';
import { tap } from 'rxjs/operators';

export interface CustomerRegistrationCompleteRequest {
    personalCode: string;
    phone: {
        code: string;
        number: string;
    };
}

@Injectable()
export class CustomerService {
    constructor(
        @Inject('ENV') private env: Env,
        private http: HttpClient,
        private translate: TranslateService,
        private router: Router,
        private authService: AuthService
    ) {}

    fetchCustomerProfile(token: string = null): Observable<any> {
        if (this.env.WMB?.AUTH) {
            const profileUrl = `${this.env.WMB.WMB_PUBLIC_API}/v1/auth/profile`;
            return this.http.get(profileUrl);
        }
        let headers = new HttpHeaders();
        headers = headers.set('Authorization', `Bearer ${token}`);
        if (!token) {
            headers = null;
        }
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/customer-account`;
        return this.http.get(url, { headers });
    }

    identifyCustomerSuomiSSO(): Observable<RedirectSSOUrlResponse> {
        let headers = new HttpHeaders();
        headers = headers.set('Accept-Language', this.translate.currentLang);
        const url = `${this.env.API_GATEWAY_URL}/identity/v1/suomi/${this.env.REGION_ID}/identification/redirect-url`;
        return this.http.get<RedirectSSOUrlResponse>(url, { headers });
    }

    saveCustomerProfile(payload: SetProfileInformationDTO | SetCustomerProfileInformation): Observable<any> {
        if (this.env.IS_NEW_AFC && this.env.WMB) {
            const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/afc-customers/account`;
            return this.http.put(url, payload);
        } else {
            const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/customer-account`;
            return this.http.put(url, payload);
        }
    }

    completeCustomerRegistration(payload: CustomerRegistrationCompleteRequest): Observable<any> {
        if (this.env.WMB?.AUTH) {
            const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/afc-customers/account/complete-registration`;
            return this.http.put(url, payload);
        }
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/account/complete-registration`;
        return this.http.put(url, payload);
    }

    saveCustomerProfileWithAddress(profile, address): Observable<any> {
        const url1 = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/customer-account`;
        const url2 = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/customer-account/address`;

        return forkJoin([this.http.put(url2, address), this.http.put(url1, profile)]);
    }

    forgetCustomer() {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/customer-account/forget`;
        const payload = [];
        return this.http.post(url, payload);
    }

    verifyCustomerEmail(token) {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/customer-account/email/${token}`;
        const payload = [];
        return this.http.put(url, payload);
    }

    changeCustomerEmail(payload) {
        let headers = new HttpHeaders();
        headers = headers.set('X-app-lang', this.translate.currentLang);

        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/customer-account/email`;
        return this.http.put(url, payload, { headers: headers });
    }

    setCustomerEmail(payload: { lang: string; email: string; redirectUri: string }): Observable<void> {
        return this.http.put<void>(
            `${this.env.API_GATEWAY_URL}/v3/region/${this.env.REGION_ID}/customer/email`,
            payload
        );
    }

    confirmCustomerEmail(payload: { code: string }): Observable<void> {
        return this.http.post<void>(
            `${this.env.API_GATEWAY_URL}/v3/region/${this.env.REGION_ID}/customer/email/confirm`,
            payload
        );
    }

    sendCustomerLang(payload) {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/customer-account/lang`;
        return this.http.put(url, payload);
    }

    sendCustomerProfileDeletionEmail() {
        let headers = new HttpHeaders();
        headers = headers.set('X-app-lang', this.translate.currentLang);

        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/customer-account/forget/email-confirmation`;
        return this.http.post(url, [], { headers: headers });
    }

    deleteCustomerProfile(token) {
        let url: string;
        let headers = new HttpHeaders();

        if (this.env.IS_NEW_AFC) {
            url = `${this.env.WMB.WMB_PUBLIC_API}/v1/afc-customers/account`;
            if (token) {
                headers = headers.set('Authorization', `Bearer ${token}`);
            }
            this.authService.isAccountDeleted = true;
            return this.http.delete(url, { headers: headers }).pipe(
                tap(() => {
                    const path = this.env.DEFAULT_REDIRECT_AFTER_ACCOUNT_DELETE ?? ['login'];
                    this.router.navigate([this.translate.currentLang, ...path]);
                    this.authService.isAccountDeleted = false;
                })
            );
        } else {
            url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/customer-account/forget/${token}`;
            if (token === '') {
                url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/customer-account/forget`;
            }
            headers = headers.set('X-app-lang', this.translate.currentLang);
        }

        return this.http.delete(url, { headers: headers });
    }
}
